import { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { copyChatDebugText } from '../lib/chat-debug'

interface ChatDebugCopyButtonProps {
	text: string | (() => string)
	label: string
	visibleLabel?: string
	compact?: boolean
}

export const ChatDebugCopyButton = ({ text, label, visibleLabel, compact = false }: ChatDebugCopyButtonProps) => {
	const [isCopied, setIsCopied] = useState(false)
	const resetTimerRef = useRef<number | null>(null)

	useEffect(
		() => () => {
			if (resetTimerRef.current !== null) window.clearTimeout(resetTimerRef.current)
		},
		[],
	)

	const handleCopy = async () => {
		try {
			await copyChatDebugText(typeof text === 'function' ? text() : text)
			setIsCopied(true)
			if (resetTimerRef.current !== null) window.clearTimeout(resetTimerRef.current)
			resetTimerRef.current = window.setTimeout(() => setIsCopied(false), 1600)
		} catch {
			toast.error('Не удалось скопировать')
		}
	}

	const accessibleLabel = isCopied ? 'Скопировано' : label

	return (
		<button
			type="button"
			className={['coach-chat-debug-copy', compact ? 'is-compact' : '', isCopied ? 'is-copied' : '']
				.filter(Boolean)
				.join(' ')}
			onClick={handleCopy}
			aria-label={accessibleLabel}
			title={accessibleLabel}
		>
			{isCopied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
			{compact ? null : <span>{isCopied ? 'Готово' : visibleLabel || label}</span>}
		</button>
	)
}

interface ChatSessionDebugBarProps {
	enabled: boolean
	sessionId: string | null
	copyText?: string | (() => string)
	isConnecting: boolean
}

export const ChatSessionDebugBar = ({ enabled, sessionId, copyText, isConnecting }: ChatSessionDebugBarProps) => {
	if (!enabled) return null

	const visibleSessionId = sessionId || (isConnecting ? 'resolving session…' : 'session unavailable')

	return (
		<aside className="coach-chat-debug-session" aria-label="Отладочная информация чата">
			<span className="coach-chat-debug-environment">dev</span>
			<code title={sessionId || undefined}>{visibleSessionId}</code>
			{sessionId ? (
				<ChatDebugCopyButton
					text={copyText || sessionId}
					label="Скопировать данные сессии"
					compact
				/>
			) : (
				<span
					className={['coach-chat-debug-pulse', isConnecting ? '' : 'is-idle'].filter(Boolean).join(' ')}
					aria-hidden="true"
				/>
			)}
		</aside>
	)
}
